import React, {
  createContext,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import Select, { StylesConfig } from 'react-select'
import { Form } from 'react-bootstrap'
import { useAppDispatch, useAppSelector } from 'src/redux/storeHooks'
import {
  getOmsShippingCarrierFieldAsync,
  getOmsShippingCarrierOptionsAsync,
} from 'src/redux/actions/omsShippingActions'

// ── Types ──

type CarrierOption = {
  value: string // api_id e.g. "99MINUTOS"
  label: string // api_name e.g. "99 Minutos"
  isOthers?: boolean
}

type ShippingCarrierSelectProps = {
  value: string // current api_id value or 'others'
  customValue: string // custom text when 'others' is selected
  onChange: (apiId: string, customText: string) => void
  disabled?: boolean
  isSubmitting?: boolean
}

// ── Constants ──

const OTHERS_OPTION: CarrierOption = { value: 'others', label: 'Others', isOthers: true }
const SEARCH_DEBOUNCE_MS = 400
const MIN_SEARCH_LENGTH = 1

// ── Helpers ──

const isAbortedRequest = (error: any): boolean =>
  error?.name === 'AbortError' ||
  error?.name === 'CanceledError' ||
  error?.code === 'ERR_CANCELED'

const getResponseResults = (payload: any): any[] => {
  if (Array.isArray(payload?.results)) return payload.results
  const data = payload?.data ?? payload
  if (Array.isArray(data)) return data
  if (Array.isArray(data?.results)) return data.results
  return []
}

const getNextUrl = (payload: any): string | null => {
  const next = payload?.next ?? payload?.data?.next
  return typeof next === 'string' && next.length > 0 ? next : null
}

const toCarrierOption = (item: any): CarrierOption | null => {
  if (!item || !item.api_id) return null
  return {
    value: item.api_id,
    label: item.api_name ?? item.api_id,
  }
}

const mergeUniqueOptions = (options: CarrierOption[]): CarrierOption[] => {
  const seen = new Set<string>()
  return options.filter((opt) => {
    if (seen.has(opt.value)) return false
    seen.add(opt.value)
    return true
  })
}


// ── Custom styles to match the existing Form.Select look ──

const selectStyles: StylesConfig<CarrierOption, false> = {
  control: (base, state) => ({
    ...base,
    fontSize: '14px',
    minHeight: '34px',
    height: '34px',
    borderColor: state.isFocused ? '#86b7fe' : '#ced4da',
    boxShadow: state.isFocused ? '0 0 0 0.25rem rgba(13,110,253,.25)' : 'none',
    '&:hover': { borderColor: state.isFocused ? '#86b7fe' : '#ced4da' },
  }),
  valueContainer: (base) => ({
    ...base,
    padding: '0 8px',
  }),
  dropdownIndicator: (base) => ({
    ...base,
    padding: '4px 8px',
  }),
  clearIndicator: (base) => ({
    ...base,
    padding: '4px 8px',
  }),
  input: (base) => ({
    ...base,
    margin: '0',
    padding: '0',
  }),
  menu: (base) => ({
    ...base,
    zIndex: 9999,
    fontSize: '14px',
  }),
  menuList: (base) => ({
    ...base,
    paddingBottom: 0, // Remove padding so sticky element is flush with bottom
  }),
  option: (base, state) => ({
    ...base,
    fontSize: '14px',
    backgroundColor: state.isSelected
      ? '#0d6efd'
      : state.isFocused
      ? '#e9ecef'
      : state.data?.isOthers
      ? '#fff' // Opaque background to prevent text overlap while scrolling
      : 'transparent',
    color: state.isSelected ? '#fff' : '#212529',
    borderTop: state.data?.isOthers ? '1px solid #dee2e6' : 'none',
    fontStyle: state.data?.isOthers ? 'italic' : 'normal',
    position: state.data?.isOthers ? 'sticky' : undefined,
    bottom: state.data?.isOthers ? 0 : undefined,
    zIndex: state.data?.isOthers ? 10 : undefined,
    '&:active': { backgroundColor: '#0d6efd', color: '#fff' },
  }),
  placeholder: (base) => ({ ...base, color: '#adb5bd', opacity: 0.6 }),
  singleValue: (base) => ({ ...base, color: '#212529' }),
  indicatorSeparator: () => ({ display: 'none' }),
}

// ── Main Component ──

const ShippingCarrierSelect: FC<ShippingCarrierSelectProps> = ({
  value,
  customValue,
  onChange,
  disabled = false,
  isSubmitting = false,
}) => {
  const dispatch = useAppDispatch()

  // Redux state
  const omsPlatformId = useAppSelector((s) => s.fetchConnectedPlatform?.omsStoreData?.id ?? null)
  const carrierFieldId = useAppSelector((s) => s.omsShipping.carrierFieldId)
  const carrierFieldLoading = useAppSelector((s) => s.omsShipping.carrierFieldLoading)
  const carrierFieldResolved = useAppSelector((s) => s.omsShipping.carrierFieldResolved)

  // Local state
  const [options, setOptions] = useState<CarrierOption[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [inputValue, setInputValue] = useState('')

  // Refs for search / pagination / scroll
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const searchAbortRef = useRef<AbortController | null>(null)
  const paginationAbortRef = useRef<AbortController | null>(null)
  const activeSearchRef = useRef('')
  const nextUrlRef = useRef<string | null>(null)
  const isLoadingMoreRef = useRef(false)
  const initialFetchDoneRef = useRef(false)

  // ── Step 1: Resolve carrier field ID (once) ──
  useEffect(() => {
    if (!omsPlatformId || carrierFieldId || carrierFieldLoading || carrierFieldResolved) return
    dispatch(getOmsShippingCarrierFieldAsync({ platformId: omsPlatformId }))
  }, [omsPlatformId, carrierFieldId, carrierFieldLoading, carrierFieldResolved, dispatch])

  // ── Step 2: Fetch initial options when field ID is available ──
  useEffect(() => {
    if (!carrierFieldId || initialFetchDoneRef.current) return
    initialFetchDoneRef.current = true
    fetchCarrierOptions('')
  }, [carrierFieldId]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Cleanup on unmount ──
  useEffect(() => {
    return () => {
      cancelPendingSearch()
      paginationAbortRef.current?.abort()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const cancelPendingSearch = useCallback(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
      searchTimeoutRef.current = null
    }
    searchAbortRef.current?.abort()
    searchAbortRef.current = null
  }, [])



  // ── Fetch options (initial or search) ──
  const fetchCarrierOptions = useCallback(
    async (searchString: string) => {
      if (!carrierFieldId) return

      const abortController = new AbortController()
      cancelPendingSearch()
      paginationAbortRef.current?.abort()
      paginationAbortRef.current = null
      searchAbortRef.current = abortController
      activeSearchRef.current = searchString.trim()

      setIsLoading(true)

      try {
        const response = await dispatch(
          getOmsShippingCarrierOptionsAsync({
            fieldId: carrierFieldId,
            search_string: searchString.trim(),
            signal: abortController.signal,
          })
        )

        if (abortController.signal.aborted) return

        if (getOmsShippingCarrierOptionsAsync.rejected.match(response)) {
          if (isAbortedRequest(response.error) || isAbortedRequest(response.payload)) return
        }

        const results = getResponseResults(response.payload)
        const carrierOptions = results.map(toCarrierOption).filter(Boolean) as CarrierOption[]
        setOptions(carrierOptions)
        nextUrlRef.current = getNextUrl(response.payload)
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoading(false)
        }
      }
    },
    [cancelPendingSearch, carrierFieldId, dispatch]
  )

  // ── Load more (infinite scroll) ──
  const loadMoreOptions = useCallback(async () => {
    const currentNextUrl = nextUrlRef.current
    if (!currentNextUrl || isLoadingMoreRef.current) return

    const abortController = new AbortController()
    paginationAbortRef.current?.abort()
    paginationAbortRef.current = abortController
    isLoadingMoreRef.current = true

    try {
      const response = await dispatch(
        getOmsShippingCarrierOptionsAsync({
          next_url: currentNextUrl,
          signal: abortController.signal,
        })
      )

      if (abortController.signal.aborted) return

      if (getOmsShippingCarrierOptionsAsync.rejected.match(response)) {
        if (isAbortedRequest(response.error) || isAbortedRequest(response.payload)) return
      }

      const results = getResponseResults(response.payload)
      const moreOptions = results.map(toCarrierOption).filter(Boolean) as CarrierOption[]
      setOptions((prev) => mergeUniqueOptions([...prev, ...moreOptions]))
      nextUrlRef.current = getNextUrl(response.payload)
    } finally {
      if (!abortController.signal.aborted) {
        isLoadingMoreRef.current = false
      }
    }
  }, [dispatch])



  // ── Input change (debounced search) ──
  const handleInputChange = (nextInputValue: string, actionMeta: { action: string }) => {
    if (actionMeta.action !== 'input-change') return

    setInputValue(nextInputValue)
    activeSearchRef.current = nextInputValue.trim()
    cancelPendingSearch()
    paginationAbortRef.current?.abort()
    paginationAbortRef.current = null

    // Always fetch — empty string returns initial results
    searchTimeoutRef.current = setTimeout(() => {
      fetchCarrierOptions(nextInputValue.trim())
    }, SEARCH_DEBOUNCE_MS)
  }

  // ── Build displayed options (API options + pinned "Others") ──
  const displayedOptions = useMemo(() => {
    return [...options, OTHERS_OPTION]
  }, [options])

  // ── Selected value ──
  const selectedValue = useMemo((): CarrierOption | null => {
    if (!value) return null
    if (value === 'others') return OTHERS_OPTION

    // Match from current options
    const matched = options.find((opt) => opt.value === value)
    if (matched) return matched

    // Value exists but not in current page — show it as-is
    return { value, label: value }
  }, [value, options])

  // ── Selection handler ──
  const handleChange = (selected: CarrierOption | null) => {
    if (!selected) {
      // Cleared
      onChange('', '')
      setInputValue('')
      return
    }

    if (selected.isOthers) {
      onChange('others', customValue)
    } else {
      onChange(selected.value, '')
    }
    setInputValue('')
  }

  const isFieldDisabled = disabled || isSubmitting

  if (value === 'others') {
    return (
      <div className='position-relative'>
        <Form.Control
          type='text'
          placeholder='Enter custom shipping method...'
          value={customValue}
          onChange={(e) => onChange('others', e.target.value)}
          style={{
            fontSize: '14px',
            height: '38px',
            paddingRight: '38px',
            borderColor: '#ced4da',
          }}
          disabled={isFieldDisabled}
          autoFocus
        />
        <button
          type='button'
          onClick={() => onChange('', '')}
          className='btn btn-link position-absolute end-0 top-50 translate-middle-y text-decoration-none text-muted'
          style={{
            zIndex: 5,
            padding: '8px 12px',
            border: 'none',
            background: 'none',
          }}
          title='Back to list'
        >
          <i className='bi bi-x-lg' style={{ fontSize: '13px' }}></i>
        </button>
      </div>
    )
  }

  return (
    <Select<CarrierOption, false>
      className='react-select'
      classNamePrefix='select'
      captureMenuScroll={false}
      filterOption={null}
      inputValue={inputValue}
      isClearable
      isDisabled={isFieldDisabled}
      isLoading={isLoading || carrierFieldLoading}
      isSearchable
      menuShouldScrollIntoView={false}
      noOptionsMessage={() =>
        isLoading ? 'Loading...' : 'No carriers found'
      }
      options={displayedOptions}
      placeholder='Search and select carrier...'
      styles={selectStyles}
      value={selectedValue}
      onChange={handleChange}
      onInputChange={handleInputChange}
      onMenuScrollToBottom={loadMoreOptions}
    />
  )
}

export default ShippingCarrierSelect
